import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { 
  Download, 
  RefreshCw, 
  Copy, 
  Check, 
  Presentation,
  Clock,
  BarChart3,
  Image,
  FileText,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Eye,
  X,
  ExternalLink,
  ArrowRight,
  Mic,
  Speaker,
  MessageSquare,
  Target,
  TrendingUp,
  Users,
  Lightbulb
} from 'lucide-react'
import { calculateTotalDuration } from './pptUtils'
import LoadingSkeleton from '../../components/LoadingSkeleton'
import CitationPopup from '../../components/CitationPopup'
import ChartVisualization from '../../components/ChartVisualization'
import DiagramVisualization from '../../components/DiagramVisualization'
import AgentHeader from '../../components/AgentHeader'

const PPTResultPage = ({ 
  query, 
  outline, 
  generatedContent, 
  academicSources, 
  progress, 
  isLoading, 
  isCompleted, 
  onBack, 
  onRegenerateSlide,
  error 
}) => {
  const [copiedSlides, setCopiedSlides] = useState(new Set())
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [showCitations, setShowCitations] = useState(false)
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const [copiedScript, setCopiedScript] = useState(false)

  const [downloadError, setDownloadError] = useState(null)
  const contentRef = useRef(null)

  const totalDuration = calculateTotalDuration(outline || [])
  const completedSlides = Object.keys(generatedContent || {}).length
  const totalSlides = (outline || []).length
  const progressPercentage = totalSlides > 0 ? (completedSlides / totalSlides) * 100 : 0

  const handleCopySlide = async (slideId) => {
    const slide = outline.find(s => s.id === slideId)
    const content = generatedContent[slideId]
    
    if (!slide || !content) return

    const formatValue = (value) => {
      if (typeof value === 'object' && value !== null) {
        return Array.isArray(value) ? value.join(', ') : JSON.stringify(value, null, 2)
      }
      return value || ''
    }

    const slideText = `
=== ${slide.title} ===

${formatValue(content.content?.details)}

Poin Utama:
${content.content?.mainPoints?.map(point => `• ${formatValue(point)}`).join('\n') || ''}

Catatan Presenter:
${formatValue(content.speakerNotes)}

Elemen Visual:
${content.visualElements?.map(el => `• ${el.type}: ${formatValue(el.title)} - ${formatValue(el.description)}`).join('\n') || ''}
    `.trim()

    try {
      await navigator.clipboard.writeText(slideText)
      setCopiedSlides(prev => new Set([...prev, slideId]))
      setTimeout(() => {
        setCopiedSlides(prev => {
          const newSet = new Set(prev)
          newSet.delete(slideId)
          return newSet
        })
      }, 2000)
    } catch (error) {
      console.error('Failed to copy slide:', error)
    }
  }

  // Function to download chart/visual as image using Chart.js
  const handleDownloadChart = async (element, slideTitle) => {
    try {
      // Import Chart.js dynamically with all required components
      const {
        Chart,
        CategoryScale,
        LinearScale,
        RadialLinearScale,
        BarElement,
        LineElement,
        PointElement,
        ArcElement,
        Filler,
        Title,
        Tooltip,
        Legend,
      } = await import('chart.js/auto')
      
      // Register Chart.js components
      Chart.register(
        CategoryScale,
        LinearScale,
        RadialLinearScale,
        BarElement,
        LineElement,
        PointElement,
        ArcElement,
        Filler,
        Title,
        Tooltip,
        Legend
      )
      
      // Create a temporary canvas for Chart.js
      const tempCanvas = document.createElement('canvas')
      const devicePixelRatio = window.devicePixelRatio || 1
      const displayWidth = 1200
      const displayHeight = 800
      
      // Set canvas dimensions for high quality
      tempCanvas.width = displayWidth * devicePixelRatio
      tempCanvas.height = displayHeight * devicePixelRatio
      tempCanvas.style.width = displayWidth + 'px'
      tempCanvas.style.height = displayHeight + 'px'
      
      const tempCtx = tempCanvas.getContext('2d')
      tempCtx.scale(devicePixelRatio, devicePixelRatio)
      
      // Parse chart data using the same logic as ChartVisualization
      const parseChartData = (element) => {
        try {
          let data = element.data
          
          if (typeof data === 'string') {
            try {
              data = JSON.parse(data)
            } catch {
              return {
                labels: ['Data'],
                datasets: [{
                  label: element.title || 'Data',
                  data: [1],
                  backgroundColor: 'rgba(147, 51, 234, 0.8)',
                  borderColor: 'rgba(147, 51, 234, 1)',
                  borderWidth: 1
                }]
              }
            }
          }
          
          if (!data) {
            return {
              labels: ['Item 1', 'Item 2', 'Item 3', 'Item 4'],
              datasets: [{
                label: element.title || 'Sample Data',
                data: [25, 35, 20, 20],
                backgroundColor: [
                  'rgba(147, 51, 234, 0.8)',
                  'rgba(59, 130, 246, 0.8)',
                  'rgba(16, 185, 129, 0.8)',
                  'rgba(245, 158, 11, 0.8)'
                ],
                borderColor: [
                  'rgba(147, 51, 234, 1)',
                  'rgba(59, 130, 246, 1)',
                  'rgba(16, 185, 129, 1)',
                  'rgba(245, 158, 11, 1)'
                ],
                borderWidth: 1
              }]
            }
          }
          
          if (data.labels && data.datasets) {
            return data
          }
          
          if (data.labels && data.values) {
            return {
              labels: data.labels,
              datasets: [{
                label: element.title || 'Data',
                data: data.values,
                backgroundColor: [
                  'rgba(147, 51, 234, 0.8)',
                  'rgba(59, 130, 246, 0.8)',
                  'rgba(16, 185, 129, 0.8)',
                  'rgba(245, 158, 11, 0.8)',
                  'rgba(239, 68, 68, 0.8)',
                  'rgba(139, 92, 246, 0.8)'
                ],
                borderColor: [
                  'rgba(147, 51, 234, 1)',
                  'rgba(59, 130, 246, 1)',
                  'rgba(16, 185, 129, 1)',
                  'rgba(245, 158, 11, 1)',
                  'rgba(239, 68, 68, 1)',
                  'rgba(139, 92, 246, 1)'
                ],
                borderWidth: 1
              }]
            }
          }
          
          if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
            const keys = Object.keys(data[0])
            const labelKey = keys.find(key => 
              key.toLowerCase().includes('name') || 
              key.toLowerCase().includes('label') ||
              key.toLowerCase().includes('category')
            ) || keys[0]
            
            const valueKey = keys.find(key => 
              key.toLowerCase().includes('value') ||
              key.toLowerCase().includes('amount') ||
              key.toLowerCase().includes('count')
            ) || keys[1] || keys[0]
            
            return {
              labels: data.map(item => item[labelKey] || `Item ${data.indexOf(item) + 1}`),
              datasets: [{
                label: element.title || 'Data',
                data: data.map(item => {
                  const value = item[valueKey]
                  return typeof value === 'number' ? value : parseFloat(value) || 0
                }),
                backgroundColor: [
                  'rgba(147, 51, 234, 0.8)',
                  'rgba(59, 130, 246, 0.8)',
                  'rgba(16, 185, 129, 0.8)',
                  'rgba(245, 158, 11, 0.8)',
                  'rgba(239, 68, 68, 0.8)',
                  'rgba(139, 92, 246, 0.8)'
                ],
                borderColor: [
                  'rgba(147, 51, 234, 1)',
                  'rgba(59, 130, 246, 1)',
                  'rgba(16, 185, 129, 1)',
                  'rgba(245, 158, 11, 1)',
                  'rgba(239, 68, 68, 1)',
                  'rgba(139, 92, 246, 1)'
                ],
                borderWidth: 1
              }]
            }
          }
          
          return {
            labels: ['Kategori A', 'Kategori B', 'Kategori C', 'Kategori D'],
            datasets: [{
              label: element.title || 'Sample Data',
              data: [30, 25, 25, 20],
              backgroundColor: [
                'rgba(147, 51, 234, 0.8)',
                'rgba(59, 130, 246, 0.8)',
                'rgba(16, 185, 129, 0.8)',
                'rgba(245, 158, 11, 0.8)'
              ],
              borderColor: [
                'rgba(147, 51, 234, 1)',
                'rgba(59, 130, 246, 1)',
                'rgba(16, 185, 129, 1)',
                'rgba(245, 158, 11, 1)'
              ],
              borderWidth: 1
            }]
          }
        } catch (error) {
          console.error('Error parsing chart data:', error)
          return {
            labels: ['Data Sample'],
            datasets: [{
              label: element.title || 'Chart',
              data: [100],
              backgroundColor: 'rgba(147, 51, 234, 0.8)',
              borderColor: 'rgba(147, 51, 234, 1)',
              borderWidth: 1
            }]
          }
        }
      }
      
      // Determine chart type - EXACT SAME LOGIC AS ChartVisualization.jsx
      const getChartType = (element) => {
        // First check if chartType is explicitly provided
        if (element.chartType) {
          return element.chartType;
        }
        
        const description = (element.description || '').toLowerCase();
        const title = (element.title || '').toLowerCase();
        const text = `${description} ${title}`;
        
        // Prioritas berdasarkan kata kunci spesifik - SAME AS UI
        if (text.includes('radar') || text.includes('spider') || text.includes('multi-dimensional') || text.includes('kompetitif') || text.includes('360')) {
          return 'radar';
        }
        if (text.includes('scatter') || text.includes('bubble') || text.includes('positioning') || text.includes('correlation') || text.includes('matrix')) {
          return 'scatter';
        }
        if (text.includes('area') || text.includes('timeline') || text.includes('evolution') || text.includes('adoption') || text.includes('growth')) {
          return 'area';
        }
        if (text.includes('polar') || text.includes('segmentasi') || text.includes('distribution') || text.includes('demografi')) {
          return 'polarArea';
        }
        if (text.includes('pie') || text.includes('lingkaran') || text.includes('proporsi') || text.includes('share')) {
          return 'pie';
        }
        if (text.includes('doughnut') || text.includes('donat') || text.includes('berlubang') || text.includes('nested')) {
          return 'doughnut';
        }
        if (text.includes('line') || text.includes('garis') || text.includes('trend') || text.includes('waktu') || text.includes('time')) {
          return 'line';
        }
        
        return 'bar'; // default
      }
      
      const chartData = parseChartData(element)
      const chartType = getChartType(element)
      
      // Enhanced data for different chart types - SAME AS ChartVisualization.jsx
      const getEnhancedData = (type, baseData) => {
        switch (type) {
          case 'radar':
            return {
              ...baseData,
              datasets: baseData.datasets.map((dataset, index) => ({
                ...dataset,
                backgroundColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 0.2)`,
                borderColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`,
                borderWidth: 2,
                pointBackgroundColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`,
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`
              }))
            }
          case 'scatter':
            return {
              datasets: baseData.datasets.map((dataset, index) => ({
                ...dataset,
                data: dataset.data.map((value, i) => ({
                  x: value + Math.random() * 20 - 10,
                  y: Math.random() * 100
                })),
                backgroundColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 0.6)`,
                borderColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`,
                pointRadius: 8,
                pointHoverRadius: 10
              }))
            }
          case 'area':
            return {
              ...baseData,
              datasets: baseData.datasets.map((dataset, index) => ({
                ...dataset,
                fill: true,
                backgroundColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 0.3)`,
                borderColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`,
                borderWidth: 3,
                tension: 0.4
              }))
            }
          case 'line':
            return {
              ...baseData,
              datasets: baseData.datasets.map((dataset, index) => ({
                ...dataset,
                borderColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`,
                backgroundColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 0.1)`,
                borderWidth: 3,
                tension: 0.4,
                pointBackgroundColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`,
                pointBorderColor: '#fff',
                pointBorderWidth: 2
              }))
            }
          default:
            return baseData
        }
      }
      
      // Enhanced chart options for different chart types - SAME AS ChartVisualization.jsx
      const getEnhancedOptions = (type) => {
        const baseOptions = {
          responsive: false,
          maintainAspectRatio: false,
          devicePixelRatio: devicePixelRatio,
          plugins: {
            legend: {
              position: 'top',
              labels: {
                color: '#374151',
                usePointStyle: true,
                padding: 20,
                font: {
                  size: 14
                }
              }
            },
            title: {
              display: true,
              text: element.title || 'Chart',
              color: '#1f2937',
              font: {
                size: 18,
                weight: 'bold'
              },
              padding: {
                top: 10,
                bottom: 30
              }
            }
          }
        }
        
        switch (type) {
          case 'radar':
            return {
              ...baseOptions,
              scales: {
                r: {
                  angleLines: {
                    color: 'rgba(107, 114, 128, 0.3)'
                  },
                  grid: {
                    color: 'rgba(107, 114, 128, 0.2)'
                  },
                  pointLabels: {
                    color: '#6b7280',
                    font: {
                      size: 12
                    }
                  },
                  ticks: {
                    color: '#6b7280',
                    backdropColor: 'transparent'
                  }
                }
              }
            }
          case 'scatter':
            return {
              ...baseOptions,
              scales: {
                x: {
                  type: 'linear',
                  position: 'bottom',
                  ticks: {
                    color: '#6b7280',
                    font: {
                      size: 12
                    }
                  },
                  grid: {
                    color: 'rgba(107, 114, 128, 0.1)'
                  }
                },
                y: {
                  ticks: {
                    color: '#6b7280',
                    font: {
                      size: 12
                    }
                  },
                  grid: {
                    color: 'rgba(107, 114, 128, 0.1)'
                  }
                }
              }
            }
          case 'area':
          case 'line':
            return {
              ...baseOptions,
              elements: {
                line: {
                  tension: 0.4,
                  borderWidth: 3
                },
                point: {
                  radius: 6,
                  hoverRadius: 8
                }
              },
              scales: {
                x: {
                  ticks: {
                    color: '#6b7280',
                    font: {
                      size: 12
                    }
                  },
                  grid: {
                    color: 'rgba(107, 114, 128, 0.1)'
                  }
                },
                y: {
                  ticks: {
                    color: '#6b7280',
                    font: {
                      size: 12
                    }
                  },
                  grid: {
                    color: 'rgba(107, 114, 128, 0.1)'
                  }
                }
              }
            }
          case 'bar':
            return {
              ...baseOptions,
              scales: {
                x: {
                  ticks: {
                    color: '#6b7280',
                    font: {
                      size: 12
                    }
                  },
                  grid: {
                    color: 'rgba(107, 114, 128, 0.1)'
                  }
                },
                y: {
                  ticks: {
                    color: '#6b7280',
                    font: {
                      size: 12
                    }
                  },
                  grid: {
                    color: 'rgba(107, 114, 128, 0.1)'
                  }
                }
              }
            }
          default:
            return baseOptions
        }
      }
      
      const enhancedData = getEnhancedData(chartType, chartData)
      const enhancedOptions = getEnhancedOptions(chartType)
      
      // Handle special chart types that need conversion
      let finalChartType = chartType
      let finalData = enhancedData
      
      if (chartType === 'area') {
        finalChartType = 'line'
        finalData = {
          ...enhancedData,
          datasets: enhancedData.datasets.map(dataset => ({
            ...dataset,
            fill: true,
            backgroundColor: dataset.backgroundColor || 'rgba(147, 51, 234, 0.2)',
            borderColor: dataset.borderColor || 'rgba(147, 51, 234, 1)',
          }))
        }
      } else if (chartType === 'polarArea') {
        // PolarArea is supported by Chart.js
        finalChartType = 'polarArea'
      }
      
      // Chart.js configuration
      const chartConfig = {
        type: finalChartType,
        data: finalData,
        options: enhancedOptions
      }
      
      // Create Chart.js instance
      const chart = new Chart(tempCtx, chartConfig)
      
      // Wait for chart to render
      await new Promise(resolve => {
        chart.update('none')
        setTimeout(resolve, 100)
      })
      
      // Create final canvas with white background and proper layout
      const finalCanvas = document.createElement('canvas')
      const descriptionHeight = element.description ? 80 : 40 // Space for description
      const finalHeight = displayHeight + descriptionHeight
      
      finalCanvas.width = displayWidth * devicePixelRatio
      finalCanvas.height = finalHeight * devicePixelRatio
      finalCanvas.style.width = displayWidth + 'px'
      finalCanvas.style.height = finalHeight + 'px'
      
      const finalCtx = finalCanvas.getContext('2d')
      finalCtx.scale(devicePixelRatio, devicePixelRatio)
      
      // Fill with white background
      finalCtx.fillStyle = '#ffffff'
      finalCtx.fillRect(0, 0, displayWidth, finalHeight)
      
      // Draw the chart in the main area (leave space at bottom for description)
      finalCtx.drawImage(tempCanvas, 0, 0, displayWidth, displayHeight)
      
      // Add description below the chart (not overlapping)
      if (element.description) {
        finalCtx.fillStyle = '#6b7280'
        finalCtx.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        finalCtx.textAlign = 'center'
        
        const words = element.description.split(' ')
        let line = ''
        let y = displayHeight + 30 // Start below the chart
        const maxWidth = displayWidth - 40
        
        for (let n = 0; n < words.length; n++) {
          const testLine = line + words[n] + ' '
          const metrics = finalCtx.measureText(testLine)
          if (metrics.width > maxWidth && n > 0) {
            finalCtx.fillText(line.trim(), displayWidth / 2, y)
            line = words[n] + ' '
            y += 18
            // Limit to 2 lines to prevent overflow
            if (y > displayHeight + 60) break
          } else {
            line = testLine
          }
        }
        if (line.trim() && y <= displayHeight + 60) {
          finalCtx.fillText(line.trim(), displayWidth / 2, y)
        }
      }
      
      // Add timestamp at bottom right
      finalCtx.fillStyle = '#9ca3af'
      finalCtx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      finalCtx.textAlign = 'right'
      finalCtx.fillText(`Generated: ${new Date().toLocaleString('id-ID')}`, displayWidth - 20, finalHeight - 10)
      
      // Clean up Chart.js instance
      chart.destroy()
      
      // Download the image
      finalCanvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${slideTitle.replace(/[^a-zA-Z0-9]/g, '-')}-${(element.title || 'chart').replace(/[^a-zA-Z0-9]/g, '-')}.png`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }, 'image/png', 1.0)
      
    } catch (error) {
      console.error('Error downloading chart:', error)
      // Fallback to simple message if Chart.js fails
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = 800
      canvas.height = 600
      
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, 800, 600)
      
      ctx.fillStyle = '#374151'
      ctx.font = 'bold 24px Arial'
      ctx.textAlign = 'center'
      ctx.fillText('Chart Download Error', 400, 300)
      
      ctx.font = '16px Arial'
      ctx.fillText('Unable to generate chart image', 400, 340)
      
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `chart-error.png`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }, 'image/png')
    }
  }

  // Function to copy presentation script
  const handleCopyScript = async (slide, content) => {
    const script = generateSpeakerScript(slide, content)
    
    try {
      await navigator.clipboard.writeText(script)
      setCopiedScript(true)
      setTimeout(() => {
        setCopiedScript(false)
      }, 2000)
    } catch (error) {
      console.error('Failed to copy script:', error)
    }
  }

  const formatValue = (value) => {
    if (typeof value === 'object' && value !== null) {
      return Array.isArray(value) ? value.join(', ') : JSON.stringify(value, null, 2)
    }
    return value || ''
  }



  // Enhanced speaker notes generator
  const generateSpeakerScript = (slide, content) => {
    if (!content) return ''
    
    const slideNumber = (outline?.findIndex(s => s.id === slide.id) || 0) + 1
    const isFirstSlide = slideNumber === 1
    const isLastSlide = slideNumber === outline?.length
    
    let scriptParagraphs = []
    
    // Varied opening phrases
    const openingVariations = [
      `Selamat ${new Date().getHours() < 12 ? 'pagi' : new Date().getHours() < 17 ? 'siang' : 'sore'}, hadirin yang terhormat. Hari ini kita akan mengeksplorasi topik "${query}" yang memiliki relevansi tinggi dengan perkembangan terkini.`,
      `Terima kasih atas kesempatan ini. Saya akan menyajikan pembahasan mendalam tentang "${query}" yang telah disiapkan khusus untuk audiens yang luar biasa ini.`,
      `Selamat bergabung dalam sesi presentasi hari ini. Kita akan mendalami "${query}" melalui perspektif yang fresh dan data-driven.`
    ]
    
    // Varied transition phrases
    const transitionVariations = [
      `Beralih ke ${slide.title.toLowerCase()}, mari kita eksplorasi dimensi baru dari pembahasan kita.`,
      `Selanjutnya, kita akan mengulas ${slide.title.toLowerCase()} yang menjadi kunci pemahaman selanjutnya.`,
      `Sekarang perhatian kita tertuju pada ${slide.title.toLowerCase()}, sebuah aspek yang sangat menarik.`,
      `Mari kita dalami ${slide.title.toLowerCase()} yang akan memperkaya wawasan kita.`
    ]
    
    // Varied content introduction phrases
    const contentIntros = [
      `Berdasarkan riset terkini,`,
      `Data menunjukkan bahwa`,
      `Temuan menarik mengungkapkan`,
      `Analisis komprehensif memperlihatkan`,
      `Studi mendalam mengindikasikan`
    ]
    
    // Opening for first slide
    if (isFirstSlide) {
      const randomOpening = openingVariations[Math.floor(Math.random() * openingVariations.length)]
      scriptParagraphs.push(randomOpening)
    }
    
    // Slide transition
    if (!isFirstSlide) {
      const randomTransition = transitionVariations[Math.floor(Math.random() * transitionVariations.length)]
      scriptParagraphs.push(randomTransition)
    }
    
    // Main content narration with varied introductions
    if (content.content?.details) {
      const randomIntro = contentIntros[Math.floor(Math.random() * contentIntros.length)]
      scriptParagraphs.push(`${randomIntro} ${content.content.details}`)
    }
    
    // Key points narration with natural flow
    if (content.content?.mainPoints) {
      const mainPoints = Array.isArray(content.content.mainPoints) ? content.content.mainPoints : [content.content.mainPoints]
      if (mainPoints.length > 0) {
        const pointIntros = [
          `Mari kita telaah beberapa poin kunci:`,
          `Terdapat aspek-aspek penting yang perlu kita pahami:`,
          `Beberapa hal fundamental yang harus diperhatikan:`,
          `Poin-poin berikut layak mendapat perhatian khusus:`
        ]
        const randomPointIntro = pointIntros[Math.floor(Math.random() * pointIntros.length)]
        scriptParagraphs.push(randomPointIntro)
        
        let pointsParagraph = ''
        mainPoints.forEach((point, index) => {
          let pointText = ''
          if (typeof point === 'object') {
            if (point.title) {
              pointText = point.title
              if (point.description) {
                pointText += `. ${point.description}`
              }
            } else if (point.description) {
              pointText = point.description
            } else if (point.text) {
              pointText = point.text
            } else {
              pointText = 'aspek penting yang memerlukan perhatian'
            }
          } else {
            pointText = String(point)
          }
          
          const pointPrefixes = [
            `Pertama,`, `Kedua,`, `Ketiga,`, `Keempat,`, `Kelima,`
          ]
          const altPrefixes = [
            `Yang pertama,`, `Berikutnya,`, `Selain itu,`, `Tidak kalah penting,`, `Terakhir,`
          ]
          
          const useAlt = Math.random() > 0.5
          const prefix = useAlt && index < altPrefixes.length ? altPrefixes[index] : 
                        index < pointPrefixes.length ? pointPrefixes[index] : `Poin ke-${index + 1},`
          
          pointsParagraph += `${prefix} ${pointText}. `
        })
        scriptParagraphs.push(pointsParagraph.trim())
      }
    }
    
    // Visual elements explanation with varied approaches
    if (content.visualElements && content.visualElements.length > 0) {
      const visualIntros = [
        `Perhatikan visualisasi berikut yang menggambarkan konsep kita dengan jelas.`,
        `Grafik dan diagram pada slide ini memberikan perspektif visual yang menarik.`,
        `Mari kita amati representasi visual yang mendukung pembahasan kita.`,
        `Elemen visual berikut membantu memperjelas konsep yang sedang dibahas.`
      ]
      const randomVisualIntro = visualIntros[Math.floor(Math.random() * visualIntros.length)]
      scriptParagraphs.push(randomVisualIntro)
      
      let visualParagraph = ''
      content.visualElements.forEach((element, index) => {
        if (element.description) {
          const visualConnectors = [
            `Seperti yang terlihat,`,
            `Visualisasi menunjukkan`,
            `Dari gambar ini kita dapat melihat`,
            `Diagram memperlihatkan`
          ]
          const connector = visualConnectors[index % visualConnectors.length]
          visualParagraph += `${connector} ${element.description}. `
        }
      })
      if (visualParagraph.trim()) {
        scriptParagraphs.push(visualParagraph.trim())
      }
    }
    
    // Statistics narration with varied analysis
    if (content.statistics && content.statistics.length > 0) {
      const statsIntros = [
        `Data terbaru mengungkapkan fakta menarik:`,
        `Angka-angka berikut memberikan gambaran yang jelas:`,
        `Statistik menunjukkan tren yang signifikan:`,
        `Hasil survei memperlihatkan pola yang menarik:`
      ]
      const randomStatsIntro = statsIntros[Math.floor(Math.random() * statsIntros.length)]
      scriptParagraphs.push(randomStatsIntro)
      
      let statsParagraph = ''
      content.statistics.forEach((stat, index) => {
        const statPhrases = [
          `mencapai`,
          `berada di angka`,
          `menunjukkan nilai`,
          `tercatat sebesar`
        ]
        const phrase = statPhrases[index % statPhrases.length]
        statsParagraph += `${stat.label || stat.title || 'Indikator'} ${phrase} ${stat.value || stat.percentage || stat.number}${stat.description ? `. ${stat.description}` : ''}. `
      })
      scriptParagraphs.push(statsParagraph.trim())
    }
    
    // Key takeaways with varied emphasis
    if (content.keyTakeaways && content.keyTakeaways.length > 0) {
      const takeawayIntros = [
        `Beberapa poin penting yang perlu diingat:`,
        `Mari kita rangkum hal-hal kunci dari pembahasan ini:`,
        `Kesimpulan utama yang dapat kita ambil:`,
        `Poin-poin berikut layak mendapat perhatian khusus:`
      ]
      const randomTakeawayIntro = takeawayIntros[Math.floor(Math.random() * takeawayIntros.length)]
      scriptParagraphs.push(randomTakeawayIntro)
      
      let takeawaysParagraph = ''
      content.keyTakeaways.forEach((takeaway, index) => {
        let takeawayText = ''
        if (typeof takeaway === 'object') {
          if (takeaway.title) {
            takeawayText = takeaway.title
            if (takeaway.description) {
              takeawayText += `, ${takeaway.description}`
            }
          } else if (takeaway.description) {
            takeawayText = takeaway.description
          } else if (takeaway.message) {
            takeawayText = takeaway.message
          } else if (takeaway.text) {
            takeawayText = takeaway.text
          } else {
            takeawayText = 'insight penting'
          }
        } else {
          takeawayText = String(takeaway)
        }
        
        const takeawayPhrases = [
          `Yang pertama,`,
          `Selanjutnya,`,
          `Penting juga untuk memahami bahwa`,
          `Tidak kalah penting,`
        ]
        const phrase = takeawayPhrases[index % takeawayPhrases.length]
        takeawaysParagraph += `${phrase} ${takeawayText}. `
      })
      scriptParagraphs.push(takeawaysParagraph.trim())
    }
    
    // Recommendations with varied approaches
    if (content.recommendations && content.recommendations.length > 0) {
      const recIntros = [
        `Berdasarkan analisis ini, beberapa langkah yang dapat diambil:`,
        `Untuk tindak lanjut, saya merekomendasikan:`,
        `Langkah-langkah strategis yang perlu dipertimbangkan:`,
        `Rekomendasi untuk implementasi ke depan:`
      ]
      const randomRecIntro = recIntros[Math.floor(Math.random() * recIntros.length)]
      scriptParagraphs.push(randomRecIntro)
      
      let recsParagraph = ''
      content.recommendations.forEach((rec, index) => {
        let recText = ''
        if (typeof rec === 'object') {
          if (rec.title) {
            recText = rec.title
            if (rec.description) {
              recText += `, ${rec.description}`
            }
          } else if (rec.description) {
            recText = rec.description
          } else if (rec.details) {
            recText = rec.details
          } else {
            recText = 'langkah strategis'
          }
        } else {
          recText = String(rec)
        }
        
        const recPhrases = [
          `Pertama, perlu`,
          `Kedua, penting untuk`,
          `Selain itu, disarankan`,
          `Terakhir, sebaiknya`
        ]
        const phrase = recPhrases[index % recPhrases.length]
        recsParagraph += `${phrase} ${recText}. `
      })
      scriptParagraphs.push(recsParagraph.trim())
    }
    
    // Varied transition and closing
    if (!isLastSlide) {
      const transitions = [
        `Mari kita lanjutkan ke slide berikutnya.`,
        `Sekarang kita beralih ke topik selanjutnya.`,
        `Pembahasan dilanjutkan pada slide berikut.`,
        `Selanjutnya, mari kita bahas topik yang tidak kalah menarik.`
      ]
      const randomTransition = transitions[Math.floor(Math.random() * transitions.length)]
      scriptParagraphs.push(randomTransition)
    } else {
      const closings = [
        `Demikian presentasi hari ini. Terima kasih atas perhatiannya.`,
        `Itulah rangkuman materi presentasi ini. Semoga bermanfaat.`,
        `Sekian penjelasan dari saya. Terima kasih.`,
        `Demikian ulasan lengkap topik ini. Mari kita diskusikan lebih lanjut.`
      ]
      const randomClosing = closings[Math.floor(Math.random() * closings.length)]
      scriptParagraphs.push(randomClosing)
    }
    
    // Join paragraphs with double line breaks for better readability
    return scriptParagraphs.join('\n\n')
  }

  const renderSlideContent = (slide, content) => {
    if (!content) {
      return (
        <div className="text-center py-8 text-text-secondary">
          <Presentation className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Konten slide sedang dihasilkan...</p>
        </div>
      )
    }

    if (!slide) {
      return (
        <div className="text-center py-8 text-text-secondary">
          <div className="text-red-400 text-lg font-medium mb-2">Error</div>
          <p>Data slide tidak valid</p>
        </div>
      )
    }

    // Enhanced content structure with professional presentation standards
    const hasMainContent = content.content?.details || content.content?.description || content.content?.summary
    const hasMainPoints = content.content?.mainPoints && (
      Array.isArray(content.content.mainPoints) ? content.content.mainPoints.length > 0 : 
      typeof content.content.mainPoints === 'object' && content.content.mainPoints !== null
    )
    const hasVisualElements = content.visualElements && content.visualElements.length > 0
    const hasSpeakerNotes = content.speakerNotes
    const hasKeyTakeaways = content.keyTakeaways && content.keyTakeaways.length > 0
    const hasStatistics = content.statistics && content.statistics.length > 0
    const hasRecommendations = content.recommendations && content.recommendations.length > 0

    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Dynamic Slide Header with Varied Layouts */}
        <div className={`relative overflow-hidden rounded-xl p-3 sm:p-6 border transition-all duration-500 hover:scale-[1.02] ${
          slide.slideType === 'title' 
            ? 'bg-gradient-to-br from-indigo-600/20 via-purple-600/15 to-pink-600/20 border-indigo-500/30'
            : slide.slideType === 'content'
            ? 'bg-gradient-to-r from-emerald-500/15 via-teal-500/10 to-cyan-500/15 border-emerald-500/30'
            : slide.slideType === 'chart'
            ? 'bg-gradient-to-tr from-orange-500/15 via-amber-500/10 to-yellow-500/15 border-orange-500/30'
            : slide.slideType === 'conclusion'
            ? 'bg-gradient-to-bl from-rose-500/15 via-pink-500/10 to-purple-500/15 border-rose-500/30'
            : 'bg-gradient-to-r from-blue-500/15 via-indigo-500/10 to-purple-500/15 border-blue-500/30'
        }`}>
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16 animate-pulse" />
            <div className="absolute bottom-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 translate-y-12 animate-pulse delay-1000" />
            <div className="absolute top-1/2 left-1/2 w-16 h-16 bg-white rounded-full -translate-x-8 -translate-y-8 animate-pulse delay-500" />
          </div>
          
          <div className="relative z-10">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-4 gap-3">
              <div className="flex-1">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-text-primary mb-2 leading-tight break-words">
                  {content.content?.title || slide.title}
                </h3>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-sm">
                  <span className="flex items-center gap-2 text-text-secondary bg-black/20 px-2 sm:px-3 py-1 rounded-full">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">{slide.duration} menit</span>
                  </span>
                  <span className={`px-3 sm:px-4 py-1 sm:py-2 rounded-full font-semibold text-xs sm:text-sm ${
                    slide.slideType === 'title' ? 'bg-indigo-500/30 text-indigo-300'
                    : slide.slideType === 'content' ? 'bg-emerald-500/30 text-emerald-300'
                    : slide.slideType === 'chart' ? 'bg-orange-500/30 text-orange-300'
                    : slide.slideType === 'conclusion' ? 'bg-rose-500/30 text-rose-300'
                    : 'bg-blue-500/30 text-blue-300'
                  }`}>
                    {slide.slideType === 'title' ? '🎯 Pembuka'
                    : slide.slideType === 'content' ? '📋 Konten'
                    : slide.slideType === 'chart' ? '📊 Data & Analisis'
                    : slide.slideType === 'conclusion' ? '🎯 Kesimpulan'
                    : '📄 ' + slide.slideType}
                  </span>
                </div>
              </div>
              
              <div className="flex flex-col sm:items-end gap-2">
                <div className="flex items-center gap-2 text-green-400 bg-green-500/20 px-2 sm:px-3 py-1 rounded-full">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span className="font-medium text-xs sm:text-sm">Ready to Present</span>
                </div>
                <div className="text-xs text-text-secondary opacity-75">
                  Slide {(outline?.findIndex(s => s.id === slide.id) || 0) + 1} of {outline?.length || 1}
                </div>
              </div>
            </div>
            
            {/* Progress indicator */}
            <div className="w-full bg-black/20 rounded-full h-1 mb-2">
              <div 
                className={`h-1 rounded-full transition-all duration-1000 ${
                  slide.slideType === 'title' ? 'bg-gradient-to-r from-indigo-400 to-purple-400'
                  : slide.slideType === 'content' ? 'bg-gradient-to-r from-emerald-400 to-teal-400'
                  : slide.slideType === 'chart' ? 'bg-gradient-to-r from-orange-400 to-yellow-400'
                  : slide.slideType === 'conclusion' ? 'bg-gradient-to-r from-rose-400 to-pink-400'
                  : 'bg-gradient-to-r from-blue-400 to-indigo-400'
                }`}
                style={{ width: `${((outline?.findIndex(s => s.id === slide.id) || 0) + 1) / (outline?.length || 1) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Executive Summary - Professional Opening */}
        {hasMainContent && (
          <div className="bg-bg-tertiary rounded-lg p-3 sm:p-6 border border-border-secondary mb-4 sm:mb-6">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <div className="w-1 h-4 sm:h-6 bg-blue-500 rounded-full" />
              <h4 className="text-base sm:text-lg font-bold text-text-primary">Ringkasan Eksekutif</h4>
            </div>
            <div className="prose prose-invert max-w-none">
              {typeof content.content.details === 'object' ? (
                <div className="bg-bg-primary rounded-xl p-5 border border-border-primary shadow-sm">
                  <pre className="whitespace-pre-wrap text-sm text-text-secondary font-mono break-words">
                    {JSON.stringify(content.content.details, null, 2)}
                  </pre>
                </div>
              ) : (
                <div className="text-text-secondary leading-relaxed text-sm sm:text-base space-y-3 sm:space-y-4">
                  <div className="bg-bg-primary rounded-xl p-3 sm:p-5 border border-border-primary shadow-sm">
                    <p className="text-base sm:text-lg font-medium text-text-primary leading-relaxed break-words">
                      {content.content.details || content.content.description || content.content.summary}
                    </p>
                  </div>
                  {content.content.context && (
                    <div className="p-3 sm:p-5 bg-blue-500/15 border border-blue-500/25 rounded-xl">
                      <h5 className="font-semibold text-blue-300 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                        <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                        Konteks:
                      </h5>
                      <p className="text-text-secondary leading-relaxed text-sm sm:text-base break-words">{content.content.context}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Key Statistics - Professional Data Presentation */}
        {hasStatistics && (
          <div className="bg-bg-tertiary rounded-lg p-3 sm:p-6 border border-border-secondary mb-4 sm:mb-6">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <div className="w-1 h-4 sm:h-6 bg-orange-500 rounded-full" />
              <h4 className="text-base sm:text-lg font-bold text-text-primary">Statistik Kunci</h4>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
              {content.statistics.map((stat, index) => (
                <div key={index} className="bg-bg-primary rounded-xl p-3 sm:p-5 border border-border-primary hover:border-orange-500/40 hover:bg-orange-500/5 transition-all duration-300 shadow-sm">
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <h5 className="font-semibold text-text-primary text-sm sm:text-base leading-tight break-words flex-1 mr-2">
                      {stat.label || stat.title || `Statistik ${index + 1}`}
                    </h5>
                    <div className="w-2.5 h-2.5 bg-orange-500 rounded-full shadow-sm flex-shrink-0" />
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-orange-400 mb-2 leading-none break-all">
                    {stat.value || stat.percentage || stat.number || 'N/A'}
                  </div>
                  {stat.description && (
                    <p className="text-text-secondary text-xs sm:text-sm leading-relaxed mb-2 sm:mb-3 break-words">
                      {stat.description}
                    </p>
                  )}
                  {stat.trend && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                      <span className="text-xs text-text-primary font-medium">Tren:</span>
                      <span className={`inline-flex items-center text-xs px-2 sm:px-2.5 py-1 rounded-full border w-fit ${
                        stat.trend.toLowerCase().includes('naik') || stat.trend.toLowerCase().includes('up')
                          ? 'bg-green-500/20 text-green-300 border-green-500/30'
                          : stat.trend.toLowerCase().includes('turun') || stat.trend.toLowerCase().includes('down')
                          ? 'bg-red-500/20 text-red-300 border-red-500/30'
                          : 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-1 sm:mr-1.5 flex-shrink-0 ${
                          stat.trend.toLowerCase().includes('naik') || stat.trend.toLowerCase().includes('up')
                            ? 'bg-green-400'
                            : stat.trend.toLowerCase().includes('turun') || stat.trend.toLowerCase().includes('down')
                            ? 'bg-red-400'
                            : 'bg-yellow-400'
                        }`}></span>
                        <span className="break-words">{stat.trend}</span>
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Key Takeaways - Executive Summary */}
        {hasKeyTakeaways && (
          <div className="bg-bg-tertiary rounded-lg p-3 sm:p-6 border border-border-secondary mb-4 sm:mb-6">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <div className="w-1 h-4 sm:h-6 bg-yellow-500 rounded-full" />
              <h4 className="text-base sm:text-lg font-bold text-text-primary">Poin Penting</h4>
            </div>
            <div className="space-y-3 sm:space-y-4">
              {content.keyTakeaways.map((takeaway, index) => (
                <div key={index} className="flex items-start gap-3 sm:gap-4 p-3 sm:p-5 bg-yellow-500/8 border border-yellow-500/25 rounded-xl hover:bg-yellow-500/12 hover:border-yellow-500/35 transition-all duration-300">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-yellow-500 to-orange-500 text-black rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shadow-lg">
                    !
                  </div>
                  <div className="flex-1 min-w-0">
                    {typeof takeaway === 'object' ? (
                      <div className="space-y-2 sm:space-y-3">
                        {takeaway.title && (
                          <h5 className="font-semibold text-text-primary text-sm sm:text-lg leading-tight break-words">{takeaway.title}</h5>
                        )}
                        {takeaway.description && (
                          <p className="text-text-secondary leading-relaxed text-xs sm:text-base break-words">{takeaway.description}</p>
                        )}
                        {takeaway.impact && (
                          <div className="mt-2 sm:mt-3 p-2 sm:p-3 bg-yellow-500/15 border border-yellow-500/25 rounded-lg">
                            <span className="text-yellow-300 text-xs sm:text-sm font-medium flex items-center gap-2 break-words">
                              <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full flex-shrink-0"></span>
                              <span className="break-words">Dampak: {takeaway.impact}</span>
                            </span>
                          </div>
                        )}
                        {(takeaway.importance || takeaway.priority) && (
                          <div className="mt-2 sm:mt-3">
                            <span className="inline-flex items-center text-xs bg-yellow-500/25 text-yellow-300 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border border-yellow-500/30 w-fit">
                              <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full mr-1 sm:mr-2 flex-shrink-0"></span>
                              <span className="break-words">Prioritas: {takeaway.importance || takeaway.priority}</span>
                            </span>
                          </div>
                        )}
                        {!takeaway.title && !takeaway.description && !takeaway.message && !takeaway.text && (
                          <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
                            <pre className="text-sm text-text-secondary font-mono whitespace-pre-wrap break-words">
                              {JSON.stringify(takeaway, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-text-primary font-medium leading-relaxed text-base mb-2">
                        {takeaway}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations - Strategic Actions */}
        {hasRecommendations && (
          <div className="bg-bg-tertiary rounded-lg p-3 sm:p-6 border border-border-secondary mb-4 sm:mb-6">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <div className="w-1 h-4 sm:h-6 bg-indigo-500 rounded-full" />
              <h4 className="text-base sm:text-lg font-bold text-text-primary">Rekomendasi Strategis</h4>
            </div>
            <div className="space-y-3 sm:space-y-5">
              {content.recommendations.map((rec, index) => (
                <div key={index} className="bg-bg-primary rounded-xl p-3 sm:p-5 border border-border-primary hover:border-indigo-500/40 hover:bg-indigo-500/5 transition-all duration-300 shadow-sm">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm shadow-lg">
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h5 className="font-semibold text-text-primary mb-2 sm:mb-3 text-sm sm:text-lg break-words">
                        {typeof rec === 'object' ? rec.title || rec.action || `Rekomendasi ${index + 1}` : `Rekomendasi ${index + 1}`}
                      </h5>
                      <p className="text-text-secondary leading-relaxed mb-3 sm:mb-4 text-xs sm:text-base break-words">
                        {typeof rec === 'object' ? rec.description || rec.details || JSON.stringify(rec) : rec}
                      </p>
                      {typeof rec === 'object' && (rec.priority || rec.timeline || rec.impact) && (
                        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3">
                          {rec.priority && (
                            <span className={`inline-flex items-center text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border w-fit ${
                              rec.priority.toLowerCase() === 'tinggi' || rec.priority.toLowerCase() === 'high'
                                ? 'bg-red-500/20 text-red-300 border-red-500/30'
                                : rec.priority.toLowerCase() === 'sedang' || rec.priority.toLowerCase() === 'medium'
                                ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                                : 'bg-green-500/20 text-green-300 border-green-500/30'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full mr-1 sm:mr-2 flex-shrink-0 ${
                                rec.priority.toLowerCase() === 'tinggi' || rec.priority.toLowerCase() === 'high'
                                  ? 'bg-red-400'
                                  : rec.priority.toLowerCase() === 'sedang' || rec.priority.toLowerCase() === 'medium'
                                  ? 'bg-yellow-400'
                                  : 'bg-green-400'
                              }`}></span>
                              <span className="break-words">Prioritas: {rec.priority}</span>
                            </span>
                          )}
                          {rec.timeline && (
                            <span className="inline-flex items-center text-xs bg-blue-500/20 text-blue-300 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border border-blue-500/30 w-fit">
                              <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-1 sm:mr-2 flex-shrink-0"></span>
                              <span className="break-words">Timeline: {rec.timeline}</span>
                            </span>
                          )}
                          {rec.impact && (
                            <span className="inline-flex items-center text-xs bg-purple-500/20 text-purple-300 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border border-purple-500/30 w-fit">
                              <span className="w-1.5 h-1.5 bg-purple-400 rounded-full mr-1 sm:mr-2 flex-shrink-0"></span>
                              <span className="break-words">Dampak: {rec.impact}</span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Enhanced Speaker Notes - Professional Presentation Guide */}
        <div className="bg-bg-tertiary rounded-lg p-3 sm:p-6 border border-border-secondary">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <div className="w-1 h-4 sm:h-6 bg-cyan-500 rounded-full" />
            <h4 className="text-base sm:text-lg font-bold text-text-primary flex items-center gap-2">
              <Mic className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
              <span className="break-words">Panduan Presenter</span>
            </h4>
          </div>
          
          {/* Speaking Script */}
          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="flex-shrink-0">
                <Speaker className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 mt-1" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3 gap-2">
                  <h5 className="font-semibold text-cyan-400 flex items-center gap-2 text-sm sm:text-base">
                    <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="break-words">Skrip Presentasi:</span>
                  </h5>
                  <button
                    onClick={() => handleCopyScript(slide, content)}
                    className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 text-xs bg-cyan-600 text-white rounded hover:bg-cyan-700 transition-colors w-fit"
                  >
                    {copiedScript ? (
                      <><Check className="w-3 h-3" /> <span className="hidden sm:inline">Tersalin</span></>
                    ) : (
                      <><Copy className="w-3 h-3" /> <span className="hidden sm:inline">Salin Teks</span></>
                    )}
                  </button>
                </div>
                <div className="bg-bg-primary rounded-lg p-3 sm:p-4 border border-border-primary">
                  <p className="text-text-secondary leading-relaxed text-xs sm:text-sm whitespace-pre-line break-words">
                    {generateSpeakerScript(slide, content)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Presentation Tips */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="bg-bg-primary rounded-lg p-3 sm:p-4 border border-border-primary">
              <h6 className="font-semibold text-text-primary mb-2 flex items-center gap-2 text-sm sm:text-base">
                <Target className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 flex-shrink-0" />
                <span className="break-words">Tips Presentasi</span>
              </h6>
              <ul className="text-xs sm:text-sm text-text-secondary space-y-1">
                <li className="break-words">• Jaga kontak mata dengan audiens</li>
                <li className="break-words">• Gunakan gesture yang natural</li>
                <li className="break-words">• Berikan jeda setelah poin penting</li>
                <li className="break-words">• Sesuaikan tempo bicara dengan konten</li>
              </ul>
            </div>
            
            <div className="bg-bg-primary rounded-lg p-3 sm:p-4 border border-border-primary">
              <h6 className="font-semibold text-text-primary mb-2 flex items-center gap-2 text-sm sm:text-base">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400 flex-shrink-0" />
                <span className="break-words">Engagement Audiens</span>
              </h6>
              <ul className="text-xs sm:text-sm text-text-secondary space-y-1">
                <li className="break-words">• Ajukan pertanyaan retoris</li>
                <li className="break-words">• Gunakan contoh yang relatable</li>
                <li className="break-words">• Berikan waktu untuk refleksi</li>
                <li className="break-words">• Siapkan Q&A di akhir</li>
              </ul>
            </div>
          </div>
          
          {/* Original Speaker Notes if available */}
          {hasSpeakerNotes && (
            <div className="mt-4 bg-bg-primary rounded-lg p-4 border border-border-primary">
              <h6 className="font-semibold text-text-primary mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-yellow-400" />
                Catatan Tambahan:
              </h6>
              <div className="prose prose-invert max-w-none">
                {typeof content.speakerNotes === 'object' ? (
                  <pre className="text-sm text-text-secondary font-mono bg-bg-secondary p-3 rounded border border-border-secondary">
                    {JSON.stringify(content.speakerNotes, null, 2)}
                  </pre>
                ) : (
                  <p className="text-text-secondary leading-relaxed text-sm">
                    {content.speakerNotes}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Professional Conclusion Section */}
        <div className="bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-green-500/10 rounded-lg p-6 border border-purple-500/20">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-6 bg-gradient-to-b from-purple-500 to-blue-500 rounded-full" />
            <h4 className="text-lg font-bold text-text-primary">Kesimpulan & Langkah Selanjutnya</h4>
          </div>
          
          <div className="grid gap-4 md:grid-cols-2">
            {/* Summary */}
            <div className="bg-bg-primary rounded-lg p-4 border border-border-primary">
              <h5 className="font-semibold text-text-primary mb-3 flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full" />
                Ringkasan Presentasi
              </h5>
              <div className="space-y-2 text-sm text-text-secondary">
                <p>• Presentasi telah disusun dengan struktur profesional</p>
                <p>• Konten mencakup analisis mendalam dan data pendukung</p>
                <p>• Visual elements tersedia untuk meningkatkan engagement</p>
                <p>• Speaker notes disediakan untuk panduan presenter</p>
              </div>
            </div>
            
            {/* Next Steps */}
            <div className="bg-bg-primary rounded-lg p-4 border border-border-primary">
              <h5 className="font-semibold text-text-primary mb-3 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                Langkah Selanjutnya
              </h5>
              <div className="space-y-2 text-sm text-text-secondary">
                <p>• Review dan edit konten sesuai kebutuhan</p>
                <p>• Download presentasi dalam format PowerPoint</p>
                <p>• Praktik presentasi menggunakan speaker notes</p>
                <p>• Siapkan Q&A berdasarkan konten yang disajikan</p>
              </div>
            </div>
          </div>
          
          {/* Call to Action */}
          <div className="mt-4 p-4 bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h6 className="font-semibold text-green-400 mb-1">Presentasi Siap Digunakan!</h6>
                <p className="text-text-secondary text-sm">Konten telah disusun sesuai standar presentasi profesional dengan struktur yang jelas dan data yang mendukung.</p>
              </div>
              <div className="flex items-center gap-2 text-green-400">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
                <span className="font-medium text-sm">Ready to Present</span>
              </div>
            </div>
          </div>
        </div>

        {/* Key Points - Professional Structure */}
        {hasMainPoints && (
          <div className="bg-bg-tertiary rounded-lg p-6 border border-border-secondary mb-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-1 h-6 bg-green-500 rounded-full" />
              <h4 className="text-lg font-bold text-text-primary">Poin-Poin Kunci</h4>
            </div>
            <div className="space-y-5">
              {(() => {
                const mainPoints = content.content.mainPoints;
                const pointsArray = Array.isArray(mainPoints) ? mainPoints : [mainPoints];
                
                return pointsArray.map((point, index) => (
                  <div key={index} className="flex items-start gap-4 p-5 bg-bg-primary rounded-xl border border-border-primary hover:border-green-500/40 hover:bg-green-500/5 transition-all duration-300 shadow-sm">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      {typeof point === 'object' ? (
                        <div className="space-y-3">
                          {point.title && (
                            <h5 className="font-semibold text-text-primary text-lg leading-tight">{point.title}</h5>
                          )}
                          {point.description && (
                            <p className="text-text-secondary leading-relaxed text-base">{point.description}</p>
                          )}
                          {point.impact && (
                            <div className="mt-3 p-3 bg-yellow-500/15 border border-yellow-500/25 rounded-lg">
                              <span className="text-yellow-300 text-sm font-medium flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></span>
                                Dampak: {point.impact}
                              </span>
                            </div>
                          )}
                          {!point.title && !point.description && (
                            <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
                              <pre className="text-sm text-text-secondary font-mono whitespace-pre-wrap break-words">
                                {JSON.stringify(point, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-text-primary leading-relaxed font-medium text-base">{point}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {/* Visual Elements */}
        {content.visualElements && content.visualElements.length > 0 ? (
          <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
            <h4 className="font-semibold text-text-primary mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-text-secondary" />
              Elemen Visual
            </h4>
            <div className="space-y-4">
              {content.visualElements.map((element, index) => {
                // Ensure element has required properties
                const safeElement = {
                  type: element.type || 'content',
                  title: element.title || `Visual Element ${index + 1}`,
                  description: element.description || '',
                  data: element.data || null
                }
                
                // Check if it's a chart type (including variations)
                const isChartType = safeElement.type === 'chart' || 
                                  safeElement.type === 'graph' || 
                                  safeElement.type === 'pie' || 
                                  safeElement.type === 'bar' || 
                                  safeElement.type === 'line' ||
                                  safeElement.type === 'area' ||
                                  safeElement.type === 'scatter' ||
                                  safeElement.type === 'radar' ||
                                  safeElement.type === 'polarArea' ||
                                  safeElement.type === 'doughnut' ||
                                  safeElement.chartType === 'area' ||
                                  safeElement.chartType === 'scatter' ||
                                  safeElement.chartType === 'radar' ||
                                  safeElement.chartType === 'polarArea' ||
                                  safeElement.chartType === 'pie' ||
                                  safeElement.chartType === 'doughnut' ||
                                  safeElement.chartType === 'line' ||
                                  safeElement.chartType === 'bar' ||
                                  (safeElement.description && safeElement.description.toLowerCase().includes('chart')) ||
                                  (safeElement.title && safeElement.title.toLowerCase().includes('chart')) ||
                                  (safeElement.description && (safeElement.description.toLowerCase().includes('scatter') ||
                                                              safeElement.description.toLowerCase().includes('area') ||
                                                              safeElement.description.toLowerCase().includes('radar') ||
                                                              safeElement.description.toLowerCase().includes('polar'))) ||
                                  (safeElement.title && (safeElement.title.toLowerCase().includes('scatter') ||
                                                        safeElement.title.toLowerCase().includes('area') ||
                                                        safeElement.title.toLowerCase().includes('radar') ||
                                                        safeElement.title.toLowerCase().includes('polar')))
                
                // Check if it's a diagram type
                const isDiagramType = safeElement.type === 'diagram' || 
                                    safeElement.type === 'infographic' || 
                                    safeElement.type === 'flowchart' || 
                                    safeElement.type === 'process' ||
                                    safeElement.type === 'timeline' ||
                                    (safeElement.description && (safeElement.description.toLowerCase().includes('diagram') || 
                                                            safeElement.description.toLowerCase().includes('flow') ||
                                                            safeElement.description.toLowerCase().includes('process') ||
                                                            safeElement.description.toLowerCase().includes('alur') ||
                                                            safeElement.description.toLowerCase().includes('tahap')))
                
                // Check if it's a table type
                const isTableType = safeElement.type === 'table' ||
                                  (safeElement.description && safeElement.description.toLowerCase().includes('table')) ||
                                  (safeElement.title && safeElement.title.toLowerCase().includes('table'))
                
                // Image type checking removed - no longer displaying images from linkup
                
                if (isChartType) {
                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-green-400" />
                          <span className="font-medium text-text-primary">
                            {safeElement.title}
                          </span>
                          <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs">
                            Chart
                          </span>
                        </div>
                        <button
                          onClick={() => handleDownloadChart(safeElement, slide.title)}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                        >
                          <Download className="w-3 h-3" />
                          Download
                        </button>
                      </div>
                      <div className="bg-bg-primary rounded-lg border border-border-primary overflow-hidden">
                        <ChartVisualization element={safeElement} />
                      </div>
                      {safeElement.description && (
                        <p className="text-sm text-text-secondary mt-2">
                          {safeElement.description}
                        </p>
                      )}
                    </div>
                  )
                }
                
                if (isDiagramType) {
                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-400" />
                          <span className="font-medium text-text-primary">
                            {safeElement.title}
                          </span>
                          <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs">
                            Diagram
                          </span>
                        </div>
                        <button
                          onClick={() => handleDownloadChart(safeElement, slide.title)}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          <Download className="w-3 h-3" />
                          Download
                        </button>
                      </div>
                      <div className="bg-bg-primary rounded-lg border border-border-primary overflow-hidden">
                        <DiagramVisualization element={safeElement} />
                      </div>
                      {safeElement.description && (
                        <p className="text-sm text-text-secondary mt-2">
                          {safeElement.description}
                        </p>
                      )}
                    </div>
                  )
                }
                
                // Image rendering removed - no longer displaying images from linkup
                
                if (isTableType) {
                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-orange-400" />
                          <span className="font-medium text-text-primary">
                            {safeElement.title}
                          </span>
                          <span className="bg-orange-500/20 text-orange-400 px-2 py-1 rounded text-xs">
                            Table
                          </span>
                        </div>
                        <button
                          onClick={() => handleDownloadChart(safeElement, slide.title)}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                        >
                          <Download className="w-3 h-3" />
                          Download
                        </button>
                      </div>
                      <div className="bg-bg-primary rounded-lg p-4 border border-border-primary">
                        {safeElement.data && typeof safeElement.data === 'object' ? (
                          <div className="overflow-x-auto">
                            {safeElement.data.headers && safeElement.data.rows ? (
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-border-secondary">
                                    {safeElement.data.headers.map((header, hIdx) => (
                                      <th key={hIdx} className="text-left p-2 text-text-primary font-medium">
                                        {header}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {safeElement.data.rows.map((row, rIdx) => (
                                    <tr key={rIdx} className="border-b border-border-secondary/50">
                                      {Array.isArray(row) ? row.map((cell, cIdx) => (
                                        <td key={cIdx} className="p-2 text-text-secondary">
                                          {cell}
                                        </td>
                                      )) : (
                                        <td className="p-2 text-text-secondary" colSpan={safeElement.data.headers.length}>
                                          {typeof row === 'object' ? JSON.stringify(row) : row}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div className="text-text-secondary text-sm">
                                <pre className="whitespace-pre-wrap">
                                  {JSON.stringify(safeElement.data, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-text-secondary">
                            <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>Tabel data akan ditampilkan di sini</p>
                          </div>
                        )}
                      </div>
                      {safeElement.description && (
                        <p className="text-sm text-text-secondary mt-2">
                          {safeElement.description}
                        </p>
                      )}
                    </div>
                  )
                }
                
                // For other types, create a comprehensive visual representation
                return (
                  <div key={index} className="bg-bg-primary rounded-lg p-4 border border-border-primary">
                    <div className="flex items-center gap-2 mb-3">
                      {safeElement.type === 'text' && <FileText className="w-5 h-5 text-gray-400" />}
                      {safeElement.type !== 'text' && <BarChart3 className="w-5 h-5 text-purple-400" />}
                      <span className="font-medium text-text-primary">
                        {safeElement.title}
                      </span>
                      <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-xs">
                        {safeElement.type}
                      </span>
                    </div>
                    
                    {safeElement.description && (
                      <p className="text-text-secondary text-sm mb-3">
                        {safeElement.description}
                      </p>
                    )}
                    
                    {/* Enhanced data visualization with proper JSON object handling */}
                    {safeElement.data ? (
                      <div className="bg-bg-secondary rounded-lg p-3 border border-border-secondary">
                        <div className="text-xs font-medium text-text-primary mb-2">Konten:</div>
                        {typeof safeElement.data === 'string' ? (
                          <div className="text-sm text-text-secondary bg-bg-tertiary rounded p-2">
                            {safeElement.data}
                          </div>
                        ) : Array.isArray(safeElement.data) ? (
                          <div className="space-y-2">
                            {safeElement.data.map((item, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-sm">
                                <div className="w-2 h-2 bg-purple-400 rounded-full flex-shrink-0 mt-2" />
                                <div className="text-text-secondary flex-1">
                                  {typeof item === 'object' && item !== null ? (
                                    <div className="bg-bg-primary rounded-lg p-3 border border-border-primary">
                                      {/* Handle structured object data with proper formatting */}
                            {item.title && (
                              <div className="mb-2">
                                <span className="font-semibold text-text-primary text-sm">{item.title}</span>
                              </div>
                            )}
                            {item.description && (
                              <div className="mb-2">
                                <p className="text-text-secondary text-sm leading-relaxed">{item.description}</p>
                              </div>
                            )}
                            {item.impact && (
                              <div className="mb-2">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  item.impact === 'high' || item.impact === 'tinggi' ? 'bg-red-500/20 text-red-400'
                                  : item.impact === 'medium' || item.impact === 'sedang' ? 'bg-yellow-500/20 text-yellow-400'
                                  : item.impact === 'low' || item.impact === 'rendah' ? 'bg-green-500/20 text-green-400'
                                  : 'bg-blue-500/20 text-blue-400'
                                }`}>
                                  <TrendingUp className="w-3 h-3 mr-1" />
                                  Impact: {item.impact}
                                </span>
                              </div>
                            )}
                            {/* Display other object properties with better formatting */}
                            {Object.entries(item).filter(([key]) => !['title', 'description', 'impact'].includes(key)).map(([key, value]) => (
                              <div key={key} className="mb-2 p-2 bg-bg-secondary rounded border border-border-secondary">
                                <span className="text-xs text-text-primary capitalize font-medium">{key.replace(/([A-Z])/g, ' $1').trim()}: </span>
                                <span className="text-xs text-text-secondary">
                                  {typeof value === 'object' && value !== null ? (
                                    <div className="mt-1 p-2 bg-bg-tertiary rounded text-xs">
                                      {JSON.stringify(value, null, 2)}
                                    </div>
                                  ) : (
                                    String(value)
                                  )}
                                </span>
                              </div>
                            ))}
                                    </div>
                                  ) : (
                                    <span className="text-text-secondary">{String(item)}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : typeof safeElement.data === 'object' && safeElement.data !== null ? (
                          <div className="bg-bg-primary rounded-lg p-3 border border-border-primary">
                            {/* Handle single object data with structured display */}
                            {safeElement.data.title && (
                              <div className="mb-3">
                                <h5 className="font-semibold text-text-primary">{safeElement.data.title}</h5>
                              </div>
                            )}
                            {safeElement.data.description && (
                              <div className="mb-3">
                                <p className="text-text-secondary leading-relaxed">{safeElement.data.description}</p>
                              </div>
                            )}
                            {safeElement.data.impact && (
                              <div className="mb-3">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                  safeElement.data.impact === 'high' ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                  : safeElement.data.impact === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                  : safeElement.data.impact === 'low' ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                  : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                }`}>
                                  <div className={`w-2 h-2 rounded-full mr-2 ${
                                    safeElement.data.impact === 'high' ? 'bg-red-400'
                                    : safeElement.data.impact === 'medium' ? 'bg-yellow-400'
                                    : safeElement.data.impact === 'low' ? 'bg-green-400'
                                    : 'bg-blue-400'
                                  }`} />
                                  Impact Level: {safeElement.data.impact.toUpperCase()}
                                </span>
                              </div>
                            )}
                            {/* Display other properties in a clean format */}
                            {Object.entries(safeElement.data).filter(([key]) => !['title', 'description', 'impact'].includes(key)).length > 0 && (
                              <div className="space-y-2">
                                <div className="text-xs font-medium text-text-primary mb-2">Detail Tambahan:</div>
                                {Object.entries(safeElement.data).filter(([key]) => !['title', 'description', 'impact'].includes(key)).map(([key, value]) => (
                                  <div key={key} className="flex items-start gap-2 text-sm">
                                    <span className="text-text-tertiary capitalize font-medium min-w-0 flex-shrink-0">{key}:</span>
                                    <span className="text-text-secondary flex-1">
                                      {typeof value === 'object' && value !== null ? (
                                        <div className="bg-bg-tertiary rounded p-2 text-xs">
                                          <pre className="whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</pre>
                                        </div>
                                      ) : (
                                        String(value)
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="bg-bg-tertiary rounded p-2">
                            <pre className="whitespace-pre-wrap text-xs text-text-secondary">
                              {String(safeElement.data)}
                            </pre>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-text-secondary">
                        <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Konten visual akan ditampilkan di sini</p>
                      </div>
                    )}
                  </div>
                )
              })}
             </div>
           </div>
         ) : (
           <div className="bg-gradient-to-br from-slate-800/30 to-slate-900/30 rounded-xl p-6 border border-slate-700/30 backdrop-blur-sm">
             <div className="flex items-center justify-between mb-6">
               <h4 className="text-xl font-bold text-text-primary flex items-center gap-3">
                 <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                   <BarChart3 className="w-5 h-5 text-white" />
                 </div>
                 Galeri Visual Presentasi
               </h4>
               <div className="flex items-center gap-2 text-sm text-purple-400">
                 <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
                 <span>6 Template Visual Tersedia</span>
               </div>
             </div>
             
             {/* Enhanced Visual Gallery with Varied Chart Types */}
             <div className="space-y-4 sm:space-y-6">
               {/* Row 1: Advanced Chart Types - Large Cards */}
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
                 {[
                   {
                     type: 'Line Chart',
                     title: 'Tren Pertumbuhan Multi-Series',
                     description: 'Multi-line chart dengan 4 series data menampilkan perbandingan pertumbuhan revenue, market share, user adoption, dan customer satisfaction dari 2020-2025',
                     icon: '📈',
                     gradient: 'from-blue-500 to-cyan-500',
                     bgGradient: 'from-blue-500/10 to-cyan-500/5',
                     chartType: 'line',
                     features: ['4 Data Series', 'Trend Analysis', 'Forecasting', 'Interactive Points']
                   },
                   {
                     type: 'Donut Chart',
                     title: 'Segmentasi Pasar 3D',
                     description: 'Advanced donut chart dengan nested segments menampilkan market share berdasarkan brand, price range, dan regional distribution dengan animasi 3D',
                     icon: '🍩',
                     gradient: 'from-emerald-500 to-teal-500',
                     bgGradient: 'from-emerald-500/10 to-teal-500/5',
                     chartType: 'donut',
                     features: ['Nested Segments', '3D Animation', 'Hover Effects', 'Data Drill-down']
                   }
                 ].map((visual, index) => (
                   <div 
                     key={index} 
                     className={`group relative overflow-hidden bg-gradient-to-br ${visual.bgGradient} rounded-xl p-4 sm:p-8 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:scale-105 hover:-translate-y-2 cursor-pointer backdrop-blur-sm`}
                     style={{ animationDelay: `${index * 150}ms` }}
                   >
                     {/* Animated background elements */}
                     <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                       <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${visual.gradient} rounded-full -translate-y-16 translate-x-16 opacity-20`} />
                       <div className={`absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr ${visual.gradient} rounded-full translate-y-12 -translate-x-12 opacity-20`} />
                     </div>
                     
                     <div className="relative z-10">
                       <div className="flex items-start justify-between mb-4 sm:mb-6">
                         <div className={`text-2xl sm:text-4xl p-2 sm:p-4 bg-gradient-to-br ${visual.gradient} rounded-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}>
                           {visual.icon}
                         </div>
                         <div className={`px-2 sm:px-4 py-1 sm:py-2 bg-gradient-to-r ${visual.gradient} rounded-full text-white text-xs sm:text-sm font-semibold opacity-80 group-hover:opacity-100 transition-opacity`}>
                           {visual.type}
                         </div>
                       </div>
                       
                       <h5 className="font-bold text-text-primary group-hover:text-white transition-colors text-lg sm:text-xl mb-2 sm:mb-3 break-words">
                         {visual.title}
                       </h5>
                       
                       <p className="text-xs sm:text-sm text-text-secondary group-hover:text-text-primary transition-colors leading-relaxed mb-3 sm:mb-4 break-words">
                         {visual.description}
                       </p>
                       
                       <div className="flex flex-wrap gap-1 sm:gap-2 mb-3 sm:mb-4">
                         {visual.features.map((feature, idx) => (
                           <span key={idx} className={`px-2 sm:px-3 py-1 bg-gradient-to-r ${visual.gradient} bg-opacity-20 text-xs rounded-full border border-slate-600/30 break-words`}>
                             {feature}
                           </span>
                         ))}
                       </div>
                       
                       <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-3 sm:pt-4 border-t border-slate-700/50 gap-2 sm:gap-0">
                         <div className="flex items-center gap-2">
                           <div className={`w-2 h-2 bg-gradient-to-r ${visual.gradient} rounded-full animate-pulse`} />
                           <span className="text-xs text-text-secondary group-hover:text-text-primary transition-colors">
                             Template Professional
                           </span>
                         </div>
                         <div className="flex items-center gap-1 text-xs text-text-secondary group-hover:text-text-primary transition-colors">
                           <span>Siap Pakai</span>
                         </div>
                       </div>
                     </div>
                     
                     {/* Hover effect overlay */}
                     <div className={`absolute inset-0 bg-gradient-to-br ${visual.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-xl`} />
                   </div>
                 ))}
               </div>
               
               {/* Row 2: Diverse Chart Types - 3 Column Layout */}
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                 {[
                   {
                     type: 'Bar Chart',
                     title: 'Analisis Kompetitor Multi-Axis',
                     description: 'Stacked bar chart dengan dual-axis menampilkan revenue vs market share kompetitor utama dengan breakdown quarterly performance',
                     icon: '📊',
                     gradient: 'from-purple-500 to-pink-500',
                     bgGradient: 'from-purple-500/10 to-pink-500/5',
                     chartType: 'bar',
                     features: ['Stacked Bars', 'Dual Axis', 'Quarterly Data']
                   },
                   {
                     type: 'Area Chart',
                     title: 'Timeline Adopsi Teknologi',
                     description: 'Stacked area chart menunjukkan evolusi adopsi fitur smartphone (5G, AI, Camera) dari 2018-2024 dengan proyeksi trend',
                     icon: '📈',
                     gradient: 'from-orange-500 to-amber-500',
                     bgGradient: 'from-orange-500/10 to-amber-500/5',
                     chartType: 'area',
                     features: ['Stacked Areas', 'Timeline View', 'Tech Adoption']
                   },
                   {
                     type: 'Scatter Plot',
                     title: 'Positioning Matrix Global',
                     description: 'Interactive scatter plot dengan bubble size menampilkan positioning brand berdasarkan price vs performance dengan regional clustering',
                     icon: '🎯',
                     gradient: 'from-red-500 to-rose-500',
                     bgGradient: 'from-red-500/10 to-rose-500/5',
                     chartType: 'scatter',
                     features: ['Bubble Chart', 'Clustering', 'Interactive']
                   }
                 ].map((visual, index) => (
                   <div 
                     key={index + 2} 
                     className={`group relative overflow-hidden bg-gradient-to-br ${visual.bgGradient} rounded-xl p-3 sm:p-6 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:scale-105 hover:-translate-y-2 cursor-pointer backdrop-blur-sm`}
                     style={{ animationDelay: `${(index + 2) * 100}ms` }}
                   >
                     <div className="relative z-10">
                       <div className="flex items-start justify-between mb-3 sm:mb-4">
                         <div className={`text-2xl sm:text-3xl p-2 sm:p-3 bg-gradient-to-br ${visual.gradient} rounded-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}>
                           {visual.icon}
                         </div>
                         <div className={`px-2 sm:px-3 py-1 bg-gradient-to-r ${visual.gradient} rounded-full text-white text-xs font-semibold opacity-80 group-hover:opacity-100 transition-opacity`}>
                           {visual.type}
                         </div>
                       </div>
                       
                       <h5 className="font-bold text-text-primary group-hover:text-white transition-colors text-base sm:text-lg mb-2 break-words">
                         {visual.title}
                       </h5>
                       
                       <p className="text-xs sm:text-sm text-text-secondary group-hover:text-text-primary transition-colors leading-relaxed mb-3 sm:mb-4 break-words">
                         {visual.description}
                       </p>
                       
                       {visual.features && (
                         <div className="flex flex-wrap gap-1 sm:gap-2 mb-3 sm:mb-4">
                           {visual.features.map((feature, idx) => (
                             <span key={idx} className={`px-2 py-1 bg-gradient-to-r ${visual.gradient} bg-opacity-20 text-xs rounded-full border border-slate-600/30 break-words`}>
                               {feature}
                             </span>
                           ))}
                         </div>
                       )}
                       
                       <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-2 sm:pt-3 border-t border-slate-700/50 gap-2 sm:gap-0">
                         <div className="flex items-center gap-2">
                           <div className={`w-2 h-2 bg-gradient-to-r ${visual.gradient} rounded-full animate-pulse`} />
                           <span className="text-xs text-text-secondary group-hover:text-text-primary transition-colors">
                             Ready
                           </span>
                         </div>
                         <div className="text-xs text-text-secondary group-hover:text-text-primary transition-colors">
                           Preview →
                         </div>
                       </div>
                     </div>
                     
                     <div className={`absolute inset-0 bg-gradient-to-br ${visual.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-xl`} />
                   </div>
                 ))}
               </div>
               
               {/* Row 3: Advanced Analytics Charts - 2 Column Layout */}
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
                 {[
                   {
                     type: 'Radar Chart',
                     title: 'Analisis Kompetitif 360°',
                     description: 'Multi-dimensional radar chart membandingkan 8 aspek kunci (Performance, Price, Design, Battery, Camera, Software, Brand, Support) antar kompetitor utama',
                     icon: '🎯',
                     gradient: 'from-indigo-500 to-violet-500',
                     bgGradient: 'from-indigo-500/10 to-violet-500/5',
                     chartType: 'radar',
                     features: ['8 Dimensions', '360° Analysis', 'Multi-Brand', 'Performance Score']
                   },
                   {
                     type: 'Heatmap',
                     title: 'Matrix Performa Regional',
                     description: 'Interactive heatmap menampilkan performance metrics across 15 negara dan 6 kategori produk dengan color-coded intensity dan drill-down capability',
                     icon: '🗺️',
                     gradient: 'from-emerald-500 to-cyan-500',
                     bgGradient: 'from-emerald-500/10 to-cyan-500/5',
                     chartType: 'heatmap',
                     features: ['15 Countries', 'Color Intensity', 'Interactive', 'Drill-down']
                   }
                 ].map((visual, index) => (
                   <div 
                     key={index + 5} 
                     className={`group relative overflow-hidden bg-gradient-to-br ${visual.bgGradient} rounded-xl p-4 sm:p-6 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:scale-105 hover:-translate-y-2 cursor-pointer backdrop-blur-sm`}
                     style={{ animationDelay: `${(index + 5) * 100}ms` }}
                   >
                     <div className="relative z-10">
                       <div className="flex items-start justify-between mb-3 sm:mb-4">
                         <div className={`text-2xl sm:text-3xl p-2 sm:p-3 bg-gradient-to-br ${visual.gradient} rounded-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}>
                           {visual.icon}
                         </div>
                         <div className={`px-2 sm:px-3 py-1 bg-gradient-to-r ${visual.gradient} rounded-full text-white text-xs font-semibold opacity-80 group-hover:opacity-100 transition-opacity`}>
                           {visual.type}
                         </div>
                       </div>
                       
                       <h5 className="font-bold text-text-primary group-hover:text-white transition-colors text-base sm:text-lg mb-2 break-words">
                         {visual.title}
                       </h5>
                       
                       <p className="text-xs sm:text-sm text-text-secondary group-hover:text-text-primary transition-colors leading-relaxed mb-3 sm:mb-4 break-words">
                         {visual.description}
                       </p>
                       
                       <div className="flex flex-wrap gap-1 sm:gap-2 mb-3 sm:mb-4">
                         {visual.features.map((feature, idx) => (
                           <span key={idx} className={`px-2 py-1 bg-gradient-to-r ${visual.gradient} bg-opacity-20 text-xs rounded-full border border-slate-600/30 break-words`}>
                             {feature}
                           </span>
                         ))}
                       </div>
                       
                       <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-2 sm:pt-3 border-t border-slate-700/50 gap-2 sm:gap-0">
                         <div className="flex items-center gap-2">
                           <div className={`w-2 h-2 bg-gradient-to-r ${visual.gradient} rounded-full animate-pulse`} />
                           <span className="text-xs text-text-secondary group-hover:text-text-primary transition-colors">
                             Advanced Analytics
                           </span>
                         </div>
                         <div className="text-xs text-text-secondary group-hover:text-text-primary transition-colors">
                           Interactive →
                         </div>
                       </div>
                     </div>
                     
                     <div className={`absolute inset-0 bg-gradient-to-br ${visual.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-xl`} />
                   </div>
                 ))}
               </div>
               
               {/* Row 4: Specialized Charts - 3 Column Layout */}
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                 {[
                   {
                     type: 'Combo Chart',
                     title: 'Hybrid Revenue-Growth',
                     description: 'Kombinasi bar chart (revenue) dan line chart (growth rate) dengan dual Y-axis untuk analisis comprehensive',
                     icon: '📊',
                     gradient: 'from-pink-500 to-rose-500',
                     bgGradient: 'from-pink-500/10 to-rose-500/5',
                     chartType: 'combo',
                     features: ['Dual Chart', 'Dual Y-Axis']
                   },
                   {
                     type: 'Waterfall Chart',
                     title: 'Analisis Kontribusi',
                     description: 'Waterfall chart menunjukkan breakdown kontribusi setiap faktor terhadap total market growth',
                     icon: '🌊',
                     gradient: 'from-teal-500 to-blue-500',
                     bgGradient: 'from-teal-500/10 to-blue-500/5',
                     chartType: 'waterfall',
                     features: ['Factor Analysis', 'Cumulative']
                   },
                   {
                     type: 'Gauge Chart',
                     title: 'Performance Meter',
                     description: 'Speedometer-style gauge menampilkan KPI performance dengan threshold indicators dan target zones',
                     icon: '⚡',
                     gradient: 'from-yellow-500 to-orange-500',
                     bgGradient: 'from-yellow-500/10 to-orange-500/5',
                     chartType: 'gauge',
                     features: ['KPI Meter', 'Thresholds']
                   }
                 ].map((visual, index) => (
                   <div 
                     key={index + 7} 
                     className={`group relative overflow-hidden bg-gradient-to-br ${visual.bgGradient} rounded-xl p-4 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:scale-105 hover:-translate-y-1 cursor-pointer backdrop-blur-sm`}
                     style={{ animationDelay: `${(index + 7) * 80}ms` }}
                   >
                     <div className="relative z-10">
                       <div className="flex items-start justify-between mb-3">
                         <div className={`text-2xl p-2 bg-gradient-to-br ${visual.gradient} rounded-lg group-hover:scale-110 transition-all duration-300 shadow-lg`}>
                           {visual.icon}
                         </div>
                         <div className={`px-2 py-1 bg-gradient-to-r ${visual.gradient} rounded-full text-white text-xs font-semibold opacity-80 group-hover:opacity-100 transition-opacity`}>
                           {visual.type}
                         </div>
                       </div>
                       
                       <h5 className="font-bold text-text-primary group-hover:text-white transition-colors text-sm mb-2">
                         {visual.title}
                       </h5>
                       
                       <p className="text-xs text-text-secondary group-hover:text-text-primary transition-colors leading-relaxed mb-3">
                         {visual.description}
                       </p>
                       
                       <div className="flex flex-wrap gap-1 mb-3">
                         {visual.features.map((feature, idx) => (
                           <span key={idx} className={`px-2 py-1 bg-gradient-to-r ${visual.gradient} bg-opacity-20 text-xs rounded-full border border-slate-600/30`}>
                             {feature}
                           </span>
                         ))}
                       </div>
                       
                       <div className="flex items-center justify-between pt-2 border-t border-slate-700/50">
                         <div className="flex items-center gap-1">
                           <div className={`w-1.5 h-1.5 bg-gradient-to-r ${visual.gradient} rounded-full animate-pulse`} />
                           <span className="text-xs text-text-secondary group-hover:text-text-primary transition-colors">
                             Specialized
                           </span>
                         </div>
                         <div className="text-xs text-text-secondary group-hover:text-text-primary transition-colors">
                           →
                         </div>
                       </div>
                     </div>
                     
                     <div className={`absolute inset-0 bg-gradient-to-br ${visual.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-xl`} />
                   </div>
                 ))}
               </div>
             </div>
             
             {/* Enhanced Call to Action */}
             <div className="mt-8 p-6 bg-gradient-to-r from-slate-800/50 to-slate-700/50 rounded-xl border border-slate-600/30 backdrop-blur-sm">
               <div className="flex items-center justify-between">
                 <div>
                   <h6 className="font-bold text-text-primary mb-2 flex items-center gap-2">
                     📊 <span>Advanced Chart Library</span>
                   </h6>
                   <p className="text-sm text-text-secondary leading-relaxed">
                     Koleksi lengkap 10+ jenis chart profesional: Line, Donut, Bar, Area, Scatter, Radar, Heatmap, Combo, Waterfall, dan Gauge charts dengan interaktivitas tinggi
                   </p>
                   <div className="flex flex-wrap gap-2 mt-3">
                     {['Line Chart', 'Donut Chart', 'Bar Chart', 'Area Chart', 'Scatter Plot', 'Radar Chart', 'Heatmap', 'Combo Chart', 'Waterfall', 'Gauge'].map((chart, idx) => (
                       <span key={idx} className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full border border-blue-500/30">
                         {chart}
                       </span>
                     ))}
                   </div>
                 </div>
                 <div className="flex flex-col items-end gap-2">
                   <div className="flex items-center gap-2 text-emerald-400">
                     <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                     <span className="text-sm font-medium">10+ Chart Types</span>
                   </div>
                   <div className="text-xs text-text-secondary">Interactive & Professional</div>
                   <div className="text-xs text-emerald-400 font-medium">Ready for Business</div>
                 </div>
               </div>
             </div>
           </div>
         )}

        {/* Faktor Pendorong Pasar */}
        {content.marketDrivers && content.marketDrivers.length > 0 ? (
          <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
            <h4 className="font-semibold text-text-primary mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-text-secondary" />
              Faktor Pendorong Pasar
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.marketDrivers.map((driver, index) => {
                const safeDriver = {
                  title: driver.title || driver.name || driver.factor || `Faktor ${index + 1}`,
                  description: driver.description || driver.content || driver.text || '',
                  impact: driver.impact || driver.effect || null,
                  percentage: driver.percentage || driver.value || null,
                  trend: driver.trend || driver.direction || null
                }
                
                return (
                  <div key={index} className="bg-bg-primary rounded-lg p-4 border border-border-primary hover:border-green-500/30 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg flex items-center justify-center text-sm font-bold shadow-lg">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h5 className="font-semibold text-text-primary">
                            {safeDriver.title}
                          </h5>
                          {safeDriver.percentage && (
                            <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs font-medium">
                              {safeDriver.percentage}
                            </span>
                          )}
                        </div>
                        
                        {safeDriver.description && (
                          <p className="text-text-secondary text-sm mb-3 leading-relaxed">
                            {safeDriver.description}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-4 text-xs">
                          {safeDriver.impact && (
                            <div className="flex items-center gap-1">
                              <span className="text-text-primary font-medium">Dampak:</span>
                              <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                                {safeDriver.impact}
                              </span>
                            </div>
                          )}
                          {safeDriver.trend && (
                            <div className="flex items-center gap-1">
                              <span className="text-text-primary font-medium">Tren:</span>
                              <span className={`px-2 py-1 rounded ${
                                safeDriver.trend.toLowerCase().includes('naik') || safeDriver.trend.toLowerCase().includes('up') 
                                  ? 'bg-green-500/20 text-green-400'
                                  : safeDriver.trend.toLowerCase().includes('turun') || safeDriver.trend.toLowerCase().includes('down')
                                  ? 'bg-red-500/20 text-red-400'
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {safeDriver.trend}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
            <h4 className="font-semibold text-text-primary mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-text-secondary" />
              Faktor Pendorong Pasar
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: 'Inovasi Teknologi',
                  description: 'Perkembangan teknologi AI, 5G, dan kamera canggih mendorong konsumen untuk upgrade smartphone.',
                  impact: 'Tinggi',
                  percentage: '35%',
                  trend: 'Naik'
                },
                {
                  title: 'Permintaan Konsumen',
                  description: 'Kebutuhan akan perangkat yang lebih powerful untuk gaming, fotografi, dan produktivitas.',
                  impact: 'Tinggi',
                  percentage: '30%',
                  trend: 'Stabil'
                },
                {
                  title: 'Penetrasi Internet',
                  description: 'Ekspansi jaringan internet dan digitalisasi mendorong adopsi smartphone di berbagai segmen.',
                  impact: 'Sedang',
                  percentage: '20%',
                  trend: 'Naik'
                },
                {
                  title: 'Ekonomi Digital',
                  description: 'Pertumbuhan e-commerce, fintech, dan layanan digital meningkatkan kebutuhan smartphone.',
                  impact: 'Sedang',
                  percentage: '15%',
                  trend: 'Naik'
                }
              ].map((driver, index) => (
                <div key={index} className="bg-bg-primary rounded-lg p-4 border border-border-primary hover:border-green-500/30 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg flex items-center justify-center text-sm font-bold shadow-lg">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h5 className="font-semibold text-text-primary">
                          {driver.title}
                        </h5>
                        <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs font-medium">
                          {driver.percentage}
                        </span>
                      </div>
                      
                      <p className="text-text-secondary text-sm mb-3 leading-relaxed">
                        {driver.description}
                      </p>
                      
                      <div className="flex items-center gap-4 text-xs">
                        <div className="flex items-center gap-1">
                          <span className="text-text-primary font-medium">Dampak:</span>
                          <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                            {driver.impact}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-text-primary font-medium">Tren:</span>
                          <span className={`px-2 py-1 rounded ${
                            driver.trend === 'Naik' 
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {driver.trend}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}





        {/* Speaker Notes */}
        {content.speakerNotes && (
          <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
            <h4 className="font-semibold text-text-primary mb-2 flex items-center gap-2">
              <Eye className="w-4 h-4 text-text-secondary" />
              Catatan Presenter
            </h4>
            <div className="text-text-secondary text-sm leading-relaxed">
              {typeof content.speakerNotes === 'object' ? (
                <pre className="whitespace-pre-wrap text-xs bg-bg-primary p-3 rounded border">
                  {JSON.stringify(content.speakerNotes, null, 2)}
                </pre>
              ) : (
                <p>{content.speakerNotes}</p>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  const currentSlide = outline && outline[currentSlideIndex]
  const currentContent = currentSlide ? generatedContent?.[currentSlide.id] : null

  if (error) {
    return (
      <div className="min-h-screen bg-bg-primary p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-bg-secondary rounded-xl border border-border-primary p-6">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={onBack} className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-text-secondary" />
              </button>
              <div className="flex items-center gap-2">
                <Presentation className="w-6 h-6 text-purple-500" />
                <h1 className="text-2xl font-bold text-text-primary">PPT Agent</h1>
              </div>
            </div>
            
            <div className="text-center py-8">
              <div className="text-red-400 text-lg font-medium mb-2">Terjadi Kesalahan</div>
              <div className="text-text-secondary mb-4">{error}</div>
              <button
                onClick={onBack}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Kembali
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg-primary p-2 sm:p-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-bg-secondary rounded-xl border border-border-primary p-3 sm:p-6">
          {/* Header */}
          <AgentHeader
            onBack={onBack}
            icon={Presentation}
            title="PPT Agent"
            query={`Presentasi: ${query}`}
            iconColor="text-purple-500"
            iconBgColor="bg-purple-500/10"
            actions={[]}
            additionalInfo={
              <div className="flex items-center gap-4 text-sm text-text-secondary">
                <span>{totalSlides} slide</span>
                <span>•</span>
                <span>{totalDuration} menit</span>
                <span>•</span>
                <span>{(academicSources || []).length} referensi</span>
              </div>
            }
          />

          {/* Progress Bar */}
          {!isCompleted && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-text-primary">
                  Progress: {completedSlides}/{totalSlides} slide
                </span>
                <span className="text-sm text-text-secondary">
                  {Math.round(progressPercentage)}%
                </span>
              </div>
              <div className="w-full bg-bg-tertiary rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          )}
          
          {/* Download Error */}
          {downloadError && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-red-400">
                <div className="w-4 h-4 bg-red-500 rounded-full flex-shrink-0" />
                <span className="font-medium">Error Download:</span>
              </div>
              <p className="text-red-300 mt-1">{downloadError}</p>
              <button
                onClick={() => setDownloadError(null)}
                className="mt-2 text-sm text-red-400 hover:text-red-200 underline"
              >
                Tutup
              </button>
            </div>
          )}

          {/* Action Buttons */}
          {isCompleted && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-6">
              <button
                  onClick={() => setShowCitations(true)}
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
                >
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">Lihat Referensi ({(academicSources || []).length})</span>
                  <span className="sm:hidden">Referensi ({(academicSources || []).length})</span>
                </button>
              
              <button
                onClick={() => setIsPreviewMode(!isPreviewMode)}
                className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base"
              >
                <Maximize2 className="w-4 h-4" />
                <span className="hidden sm:inline">{isPreviewMode ? 'Mode List' : 'Mode Preview'}</span>
                <span className="sm:hidden">{isPreviewMode ? 'List' : 'Preview'}</span>
              </button>
            </div>
          )}

          {/* Content Display */}
          {isLoading && completedSlides === 0 ? (
            <LoadingSkeleton 
              message="Sedang membuat konten presentasi..."
              submessage={`Memproses slide ${(progress?.current || 0) + 1} dari ${progress?.total || 0}`}
            />
          ) : (
            <div className="space-y-6">
              {isPreviewMode && isCompleted ? (
                /* Preview Mode - Single Slide View */
                <div className="bg-bg-tertiary rounded-lg p-3 sm:p-6 border border-border-secondary">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
                    <h3 className="text-base sm:text-lg font-semibold text-text-primary">
                      Slide {currentSlideIndex + 1} dari {totalSlides}
                    </h3>
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => setCurrentSlideIndex(Math.max(0, currentSlideIndex - 1))}
                        disabled={currentSlideIndex === 0}
                        className="p-2 border border-border-secondary rounded-lg hover:bg-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed text-text-secondary transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-text-secondary min-w-[60px] text-center">
                        {currentSlideIndex + 1} / {totalSlides}
                      </span>
                      <button
                        onClick={() => setCurrentSlideIndex(Math.min(totalSlides - 1, currentSlideIndex + 1))}
                        disabled={currentSlideIndex === totalSlides - 1}
                        className="p-2 border border-border-secondary rounded-lg hover:bg-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed text-text-secondary transition-colors"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {currentSlide && (
                    <div className="bg-bg-secondary rounded-lg p-3 sm:p-6 border border-border-primary">
                      {renderSlideContent(currentSlide, currentContent)}
                      
                      <div className="flex flex-col sm:flex-row gap-2 mt-4 pt-4 border-t border-border-primary">
                        <button
                          onClick={() => handleCopySlide(currentSlide.id)}
                          className="flex items-center justify-center gap-2 px-3 py-2 text-sm border border-border-secondary rounded-lg hover:bg-bg-tertiary transition-colors text-text-primary"
                        >
                          {copiedSlides.has(currentSlide.id) ? (
                            <><Check className="w-4 h-4 text-green-400" /> Tersalin</>
                          ) : (
                            <><Copy className="w-4 h-4" /> Salin</>
                          )}
                        </button>
                        
                        {currentSlide.isEditable && (
                          <button
                            onClick={() => onRegenerateSlide(currentSlide.id)}
                            disabled={isLoading}
                            className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                          >
                            <RefreshCw className="w-4 h-4" />
                            <span className="hidden sm:inline">Regenerate</span>
                            <span className="sm:hidden">Ulang</span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* List Mode - All Slides */
                (outline || []).map((slide, index) => {
                  const content = generatedContent?.[slide.id]
                  const isGenerating = isLoading && !content
                  
                  return (
                    <div key={slide.id} className="bg-bg-tertiary rounded-lg p-3 sm:p-6 border border-border-secondary">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                          <span className="bg-purple-500/20 text-purple-400 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium w-fit">
                            Slide {index + 1}
                          </span>
                          <h3 className="text-base sm:text-lg font-semibold text-text-primary break-words">{slide.title}</h3>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                          {content && (
                            <button
                              onClick={() => handleCopySlide(slide.id)}
                              className="flex items-center justify-center gap-2 px-3 py-2 text-sm border border-border-secondary rounded-lg hover:bg-bg-tertiary transition-colors text-text-primary"
                            >
                              {copiedSlides.has(slide.id) ? (
                                <><Check className="w-4 h-4 text-green-400" /> Tersalin</>
                              ) : (
                                <><Copy className="w-4 h-4" /> Salin</>
                              )}
                            </button>
                          )}
                          
                          {slide.isEditable && content && (
                            <button
                              onClick={() => onRegenerateSlide(slide.id)}
                              disabled={isLoading}
                              className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                            >
                              <RefreshCw className="w-4 h-4" />
                              <span className="hidden sm:inline">Regenerate</span>
                              <span className="sm:hidden">Ulang</span>
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="bg-bg-secondary rounded-lg p-3 sm:p-6 border border-border-primary">
                        {isGenerating ? (
                          <LoadingSkeleton 
                            message={`Sedang membuat konten untuk ${slide.title}...`}
                            submessage="Menganalisis struktur dan menghasilkan konten"
                          />
                        ) : (
                          renderSlideContent(slide, content)
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Citations Modal */}
      {showCitations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-bg-secondary rounded-lg max-w-4xl w-full max-h-[90vh] sm:max-h-[80vh] overflow-hidden border border-border-primary">
            <div className="flex items-center justify-between p-3 sm:p-6 border-b border-border-primary">
              <h2 className="text-lg sm:text-xl font-bold text-text-primary">Referensi Akademik</h2>
              <button
                onClick={() => setShowCitations(false)}
                className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-secondary" />
              </button>
            </div>
            <div className="p-3 sm:p-6 overflow-y-auto max-h-[70vh] sm:max-h-[60vh]">
              {(academicSources || []).length > 0 ? (
                <div className="space-y-3 sm:space-y-4">
                  {(academicSources || []).map((source, index) => (
                    <div key={index} className="border border-border-secondary rounded-lg p-3 sm:p-4 bg-bg-tertiary">
                      <div className="flex items-start gap-2 sm:gap-3">
                          <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-text-primary mb-2 text-sm sm:text-base break-words">
                              {source?.title || 'Untitled Source'}
                            </h3>
                            {source?.snippet && (
                              <p className="text-text-secondary text-xs sm:text-sm mb-2 leading-relaxed break-words">
                                {source.snippet}
                              </p>
                            )}
                            {(source?.url || source?.link) && (
                              <a
                                href={source.url || source.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-xs sm:text-sm transition-colors break-all"
                              >
                                <span>Buka Sumber</span>
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                              </a>
                            )}
                          </div>
                        </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8 text-text-secondary">
                  <FileText className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm sm:text-base">Tidak ada referensi yang tersedia</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

PPTResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  outline: PropTypes.array.isRequired,
  generatedContent: PropTypes.object.isRequired,
  academicSources: PropTypes.array.isRequired,
  progress: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isCompleted: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onRegenerateSlide: PropTypes.func.isRequired,
  error: PropTypes.string
}

export default PPTResultPage